import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { Box, CircularProgress, GlobalStyles } from "@mui/material";
import { useApi } from "@platform/api";
import { logger } from "@platform/browser-logger";
import { useAgGridHiddenColumns } from "@platform/hooks";
import {
  AiStatusCellRenderer,
  PlanDesigner,
  WHOLE_LIFE_DEPENDENT_AMOUNT_VALIDATION,
  queryKeys,
  useLoadRfp,
  type ValidationConfig,
} from "@platform/btx-plan-designer";
import {
  type AiRateFieldsMeta,
  BuildRateCellReviewProvider,
  type BenefitPlan,
  BenefitPlanGroup,
  RateSchedule,
  RatesDrawer,
  RatesErrorProvider,
  RatesGrid,
  SummaryGrid,
  RATE_SCHEDULES_STALE_TIME_MS,
  benefitPlanGroupQueryKeys,
  listRateSchedules,
  rateQueryKeys,
  useListBenefitPlanGroups,
} from "@platform/btx-rates";
import {
  BUILD_TAB_EXTREME_NEEDS_REVIEW_PANEL,
  NEW_DELETE_PLAN_VALIDATION,
  BTX_COMMISSION_PREPOPULATION,
} from "@platform/feature-flags";
import {
  type CoverageGroup,
  type EnrichedBenefitFieldMeta,
  buildAiFieldsByPlanId,
  useBenefitPlanFields,
  usePlanBuilderData,
} from "@platform/document-library";
import { CoverageTypeEnum } from "@platform/openapi-types";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { parseJSON } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Alert, Typography } from "@platform/design-system";

import { ROUTE_NAMES } from "../../../../constants";
import { useExitFullScreenOnEscape } from "../../../../hooks/useExitFullScreenOnEscape";
import { useRfpClientDetails } from "../../../../hooks/useRfpClientDetails";
import { useRfpDetails } from "../../../../hooks/useRfpDetails";
import useToastNotificationService from "../../../../hooks/useToastNotificationService";

import { AIAutoCount, TableLegend } from "./AiDocsComponents";
import { BenchmarkBanner } from "./BenchmarkBanner";
import { HiddenColumnsBanner } from "./HiddenColumnsBanner";

import type {
  ColumnWidthPreset,
  CoverageStatus,
} from "@platform/btx-plan-designer";
import type { AgGridReact } from "ag-grid-react";

interface RatesDrawerSelection {
  benefitPlanGroup: BenefitPlanGroup;
  benefitPlan: BenefitPlan;
}

// Ceiling on how long to keep polling for AI-extracted data after a document
// starts processing. AI extraction itself usually finishes in under a
// minute, but the extracted fields don't reach BTX until a separate
// downstream push completes afterward — observed up to ~2 minutes end to
// end (DP-4102). A document's status leaves "processing" as soon as
// extraction is done, well before that push finishes, so gating polling on
// status alone stopped it before the data existed to fetch.
const AI_DATA_POLL_CEILING_MS = 3 * 60 * 1000;
const AI_DATA_POLL_INTERVAL_MS = 3000;

// Poll for AI-extracted data every AI_DATA_POLL_INTERVAL_MS until either
// data has been fetched or AI_DATA_POLL_CEILING_MS has elapsed since a
// document started processing, whichever comes first. `processingSince` is
// null when no document has started processing during this component's
// lifetime, in which case there is nothing to poll for.
export const getAiDataRefetchInterval = (
  hasFetchedAiData: boolean,
  processingSince: number | null,
  now: number,
): number | false => {
  if (hasFetchedAiData || processingSince === null) {
    return false;
  }

  return now - processingSince < AI_DATA_POLL_CEILING_MS
    ? AI_DATA_POLL_INTERVAL_MS
    : false;
};

const getLatestDate = (current?: Date, candidate?: Date): Date | undefined => {
  if (!current) {
    return candidate;
  }

  if (!candidate) {
    return current;
  }

  return candidate.getTime() > current.getTime() ? candidate : current;
};

const parseTimestamp = (timestamp?: string): Date | undefined => {
  if (!timestamp) {
    return undefined;
  }

  const parsedTimestamp = parseJSON(timestamp);

  return Number.isNaN(parsedTimestamp.getTime()) ? undefined : parsedTimestamp;
};

const resolveLatestSavedAt = (
  rfpLastModified?: string,
  benefitPlanGroups: BenefitPlanGroup[] = [],
  rateSchedules: RateSchedule[] = [],
): Date | undefined => {
  let latestSavedAt = parseTimestamp(rfpLastModified);

  for (const group of benefitPlanGroups) {
    latestSavedAt = getLatestDate(
      latestSavedAt,
      parseTimestamp(group.updatedAt),
    );

    for (const plan of group.benefitPlans) {
      latestSavedAt = getLatestDate(
        latestSavedAt,
        parseTimestamp(plan.updatedAt),
      );
    }
  }

  for (const rateSchedule of rateSchedules) {
    latestSavedAt = getLatestDate(
      latestSavedAt,
      parseTimestamp(rateSchedule.updatedAt),
    );

    for (const rate of rateSchedule.rates ?? []) {
      latestSavedAt = getLatestDate(
        latestSavedAt,
        parseTimestamp(rate.updatedAt),
      );
    }
  }

  return latestSavedAt;
};

const getEffectiveLastSavedAt = ({
  rfpLastModified,
  isLoading,
  benefitPlanGroups,
  rateSchedules,
  lastSavedAtOverride,
}: {
  rfpLastModified?: string;
  isLoading: boolean;
  benefitPlanGroups: BenefitPlanGroup[];
  rateSchedules: RateSchedule[];
  lastSavedAtOverride?: Date;
}): Date | undefined => {
  if (isLoading) {
    return lastSavedAtOverride;
  }

  const resolvedLastSavedAt = resolveLatestSavedAt(
    rfpLastModified,
    benefitPlanGroups,
    rateSchedules,
  );

  return getLatestDate(resolvedLastSavedAt, lastSavedAtOverride);
};

/** True when every plan in every group has a policy period that carries no rates. */
const allPlansLackRates = (benefitPlanGroups: BenefitPlanGroup[]): boolean =>
  benefitPlanGroups.length > 0 &&
  benefitPlanGroups.every((group) =>
    group.benefitPlans?.every(
      (plan) =>
        plan.policyPeriod === "newBusiness" ||
        plan.policyPeriod === "alternate",
    ),
  );

/**
 * Determines which indicator mode to use for the plan editor grid.
 *
 * - "benchmark": new-business coverages with benchmark-seeded plans
 * - "ai": every other coverage (AI Docs extraction indicators)
 *
 * This is the single place that decides which indicator set is active.
 * When the rules change (e.g. AI indicators for new coverages too),
 * update only this function.
 */
type IndicatorMode = "ai" | "benchmark";

const resolveIndicatorMode = ({
  hasBenchmarkSeededPlans,
  isNewBusinessCoverage,
}: {
  hasBenchmarkSeededPlans: boolean;
  isNewBusinessCoverage: boolean;
}): IndicatorMode =>
  isNewBusinessCoverage && hasBenchmarkSeededPlans ? "benchmark" : "ai";

/** Count AI-populated cells for a single benefit row across all plans. */
const countBenefitRow = (
  benefitKey: string,
  isNetworkDependent: boolean,
  aiFieldLookup: Record<string, { value: string }>,
  benefitPlanIds: string[],
  hasAiData: boolean,
): { count: number; total: number } => {
  let populated = 0;
  let total = 0;

  for (const planId of benefitPlanIds) {
    if (isNetworkDependent) {
      total += 2;
      if (hasAiData) {
        if (aiFieldLookup[`${planId}::${benefitKey}::inNetwork`]?.value)
          populated++;
        if (aiFieldLookup[`${planId}::${benefitKey}::outOfNetwork`]?.value)
          populated++;
      }
    } else {
      total++;
      if (hasAiData && aiFieldLookup[`${planId}::${benefitKey}`]?.value) {
        populated++;
      }
    }
  }

  return { count: populated, total };
};

/** Count AI-populated benefit cells from the PlanDesigner grid rows. */
const countBenefitFields = (
  rowData: unknown[],
  aiFieldLookup: Record<string, { value: string }>,
  benefitPlanIds: string[],
): { count: number; total: number } => {
  const hasAiData = Object.keys(aiFieldLookup).length > 0;
  let populated = 0;
  let total = 0;

  for (const row of rowData) {
    const benefitKey = (row as { benefitKey?: string }).benefitKey;
    if (!benefitKey) continue;

    const isNetworkDependent =
      (row as { benefit?: { networkDependent?: boolean } }).benefit
        ?.networkDependent === true;

    const rowCounts = countBenefitRow(
      benefitKey,
      isNetworkDependent,
      aiFieldLookup,
      benefitPlanIds,
      hasAiData,
    );
    populated += rowCounts.count;
    total += rowCounts.total;
  }

  return { count: populated, total };
};

/** Count AI-populated rate fields (rate + enrolledLives) per plan. */
const countRateFields = (
  benefitPlanGroups: BenefitPlanGroup[],
  aiFieldsByPlanId?: Record<string, AiRateFieldsMeta>,
): { count: number; total: number } => {
  let populated = 0;
  let total = 0;

  for (const group of benefitPlanGroups) {
    for (const plan of group.benefitPlans) {
      total += 1;
      const rateFields = aiFieldsByPlanId?.[plan.id];
      if (rateFields?.rate?.value) populated++;
    }
  }

  return { count: populated, total };
};

/** Count AI-populated and total editable cells across benefit, rate, and summary grids. */
const countAiAutoFields = ({
  rowData,
  aiFieldLookup,
  benefitPlanIds,
  benefitPlanGroups,
  aiFieldsByPlanId,
  includeRatesAndSummary,
}: {
  rowData: unknown[];
  aiFieldLookup: Record<string, { value: string }>;
  benefitPlanIds: string[];
  benefitPlanGroups: BenefitPlanGroup[];
  aiFieldsByPlanId?: Record<string, AiRateFieldsMeta>;
  includeRatesAndSummary: boolean;
}): { count: number; total: number } => {
  const benefits = countBenefitFields(rowData, aiFieldLookup, benefitPlanIds);

  if (!includeRatesAndSummary) return benefits;

  const rates = countRateFields(benefitPlanGroups, aiFieldsByPlanId);
  // Summary grid: 2 rows (Monthly Premium, Annual Premium) × plans — no AI values
  const summaryTotal = 2 * benefitPlanIds.length;

  return {
    count: benefits.count + rates.count,
    total: benefits.total + rates.total + summaryTotal,
  };
};

/** Build a documentId -> downloadUrl map for a given coverage type from coverage groups. */
const buildDocumentDownloadUrls = (
  selectedCoverageType: string | undefined,
  coverageGroups: CoverageGroup[],
): Record<string, string> | undefined => {
  if (!selectedCoverageType || !coverageGroups.length) return undefined;
  const urls: Record<string, string> = {};
  for (const group of coverageGroups) {
    if (group.name !== selectedCoverageType) continue;
    for (const doc of group.documents) {
      if (doc.metadata.downloadUrl) {
        urls[doc.id] = doc.metadata.downloadUrl;
      }
    }
  }
  return Object.keys(urls).length > 0 ? urls : undefined;
};

/** Plan-column width as a fraction of the default: wide = 100%, medium = 65%, narrow = 35%. */
const COLUMN_WIDTH_SCALE: Record<ColumnWidthPreset, number> = {
  narrow: 0.35,
  medium: 0.65,
  wide: 1,
};

/**
 * WorkbenchBuild2 - Experimental build workbench component
 * Uses the PlanDesigner component with real BTX API data
 */
// eslint-disable-next-line complexity -- Component orchestrates multiple grids and data loading
const WorkbenchBuild2 = (): React.JSX.Element => {
  // Refs for aligned grids - enables bidirectional column sync and scroll sync
  const planDesignerGridRef = useRef<AgGridReact>(null);
  const ratesGridRef = useRef<AgGridReact>(null);
  const summaryGridRef = useRef<AgGridReact>(null);
  const [isRatesDrawerOpen, setRatesDrawerOpen] = useState(false);
  const [ratesDrawerSelection, setRatesDrawerSelection] =
    useState<RatesDrawerSelection>();
  const [lastSavedAtOverride, setLastSavedAtOverride] = useState<Date>(null);
  const [aiAutoCountValues, setAiAutoCountValues] = useState<{
    count: number;
    total: number;
  }>({ count: 0, total: 0 });
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [columnWidth, setColumnWidth] = useState<ColumnWidthPreset>("medium");
  // Rendered plan-column widths and right gutter emitted by PlanDesigner. Applied
  // to RatesGrid and SummaryGrid so they track PlanEditor's columns exactly
  // (explicit widths instead of independent flex sizing) and reserve the same
  // right gutter (PlanEditor's sidebar) — otherwise PlanEditor's narrower body
  // drifts the rate/summary columns out of alignment, most visibly when scrolled
  // to the right edge.
  const [planColumnWidths, setPlanColumnWidths] = useState<
    Record<string, number>
  >({});
  const [planRightGutter, setPlanRightGutter] = useState(0);
  const handlePlanAlignmentMetricsChange = useCallback(
    (metrics: {
      columnWidths: Record<string, number>;
      rightGutter: number;
    }) => {
      setPlanColumnWidths((prev) => {
        const widths = metrics.columnWidths;
        const keys = Object.keys(widths);
        const unchanged =
          keys.length === Object.keys(prev).length &&
          keys.every((key) => prev[key] === widths[key]);
        return unchanged ? prev : widths;
      });
      setPlanRightGutter((prev) =>
        prev === metrics.rightGutter ? prev : metrics.rightGutter,
      );
    },
    [],
  );
  const { id: rfpId, planName } = useParams<{ id: string; planName: string }>();
  const { hiddenColumnsCount } = useAgGridHiddenColumns();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const axiosInstance = useApi();
  const { createToastNotification } = useToastNotificationService();
  const newDeletePlanValidation = useFeatureIsOn(NEW_DELETE_PLAN_VALIDATION);
  const enabledBTXReviewPanel = useFeatureIsOn(
    BUILD_TAB_EXTREME_NEEDS_REVIEW_PANEL,
  );
  // When commission/contribution prepopulation owns Employer Contribution, AI
  // extraction must no longer be shown as a source for that field.
  const commissionPrepopulationEnabled = useFeatureIsOn(
    BTX_COMMISSION_PREPOPULATION,
  );

  // Fetch RFP details to get the ecoRfpId (UUID)
  const { data: rfpDetails, isLoading: isLoadingDetails } = useRfpDetails({
    rfpId,
  });

  // Fetch RFP client details to get the client name for Excel export
  const { data: clientDetails } = useRfpClientDetails({
    rfpId,
    enabled: !!rfpId,
  });

  // Fetch RFP data using React Query - automatically refetches when invalidated by mutations
  const {
    data: rfp,
    isLoading: isLoadingRfp,
    error: rfpError,
  } = useLoadRfp(rfpDetails?.ecoRfpId ?? "", axiosInstance, {
    enabled: !!rfpDetails?.ecoRfpId,
  });

  // Merge client name into RFP data for Excel export
  // The BTX API doesn't provide the RFP name, so we get it from the broker portal client details
  const rfpWithName = useMemo(
    () => (rfp ? { ...rfp, name: clientDetails?.name ?? "" } : rfp),
    [rfp, clientDetails?.name],
  );

  // Decode the URL-encoded planName parameter (e.g., "Basic%20Life%20&%20AD&D" -> "Basic Life & AD&D")
  const decodedPlanName = planName ? decodeURIComponent(planName) : null;

  // Derive selected coverage from rfp data once loaded
  const selectedCoverage = useMemo(() => {
    if (!rfpWithName) return null;
    if (!decodedPlanName) return rfpWithName.coverages[0];
    return (
      rfpWithName.coverages.find((c) => c.type === decodedPlanName) ??
      rfpWithName.coverages[0]
    );
  }, [rfpWithName, decodedPlanName]);

  const isWholeLife =
    selectedCoverage?.coverageType === CoverageTypeEnum.wholeLife;

  const validationConfig: ValidationConfig | undefined = isWholeLife
    ? WHOLE_LIFE_DEPENDENT_AMOUNT_VALIDATION
    : undefined;

  // Fetch document data from the plan builder BFF to get document IDs per coverage
  // The BFF proxies to BTX and AI Dox which both expect the ecoRfpId (UUID), not the numeric broker portal ID
  const ecoRfpId = rfpDetails?.ecoRfpId ?? "";

  const { benchmarkSeededPlanIds, hasBenchmarkSeededPlans } = useMemo(() => {
    const map: Record<string, boolean> = {};
    let hasAny = false;
    for (const plan of selectedCoverage?.benefitPlans ?? []) {
      if (plan.isBenchmarkSeeded) {
        map[plan.id] = true;
        hasAny = true;
      }
    }
    return { benchmarkSeededPlanIds: map, hasBenchmarkSeededPlans: hasAny };
  }, [selectedCoverage?.benefitPlans]);

  const { coverageGroups, isLoading: isLoadingPlanBuilderData } =
    usePlanBuilderData(ecoRfpId, {
      enabled: !!ecoRfpId,
    });

  const selectedCoverageGroup = useMemo<CoverageGroup | undefined>(() => {
    if (!selectedCoverage || !coverageGroups.length) return undefined;
    return coverageGroups.find((group) => group.name === selectedCoverage.type);
  }, [coverageGroups, selectedCoverage]);

  const selectedCoverageStatus: CoverageStatus | undefined = useMemo(() => {
    if (!selectedCoverageGroup?.status) return undefined;
    return {
      ...selectedCoverageGroup.status,
      derivedStatusKey: selectedCoverageGroup.derivedStatusKey,
    };
  }, [selectedCoverageGroup]);

  // Extract document IDs for the selected coverage type
  const documentIds = useMemo(() => {
    if (!selectedCoverageGroup) return [];
    return selectedCoverageGroup.documents.map((doc) => doc.id);
  }, [selectedCoverageGroup]);

  // Build a documentId -> downloadUrl map for the current coverage
  const documentDownloadUrls = useMemo(
    () => buildDocumentDownloadUrls(selectedCoverage?.type, coverageGroups),
    [selectedCoverage, coverageGroups],
  );

  const [hasFetchedAiData, setHasFetchedAiData] = useState(false);
  const processingSince = useRef<number | null>(null);

  // Polling exists to cover the upload race where AIDox extraction lands
  // after the first fetch. A document with no extractable fields never
  // populates the lookup, so gating on the lookup alone would poll forever;
  // poll only within a bounded window after a document starts processing.
  const anyDocumentProcessing = useMemo(
    () =>
      selectedCoverageGroup?.documents.some(
        (doc) => doc.status === "processing",
      ) ?? false,
    [selectedCoverageGroup],
  );

  // Reset the polling window whenever the selected coverage's document set
  // changes, so a stale timestamp from a previously selected coverage can't
  // suppress polling for a document that has only just started processing.
  useEffect(() => {
    processingSince.current = null;
  }, [documentIds]);

  useEffect(() => {
    if (anyDocumentProcessing && processingSince.current === null) {
      processingSince.current = Date.now();
    }
  }, [anyDocumentProcessing, documentIds]);

  // Fetch AI extraction metadata for the documents in the current coverage
  const {
    fieldLookup: aiFieldLookup,
    planDocumentMap,
    isLoading: isLoadingAiFields,
  } = useBenefitPlanFields(documentIds, {
    enabled: documentIds.length > 0,
    documentDownloadUrls,
    refetchInterval: getAiDataRefetchInterval(
      hasFetchedAiData,
      processingSince.current,
      Date.now(),
    ),
  });

  useEffect(() => {
    setHasFetchedAiData(Object.keys(aiFieldLookup).length > 0);
  }, [aiFieldLookup]);

  // Merge commission parse errors into aiFieldLookup so the amber warning icon
  // appears on commission cells whose broker portal strings could not be parsed.
  const aiFieldLookupWithParseErrors = useMemo(() => {
    const parseErrors = rfp?.broker?.parseErrors;
    if (!parseErrors || parseErrors.length === 0) return aiFieldLookup;

    const parseErrorMeta = (
      btxFieldName: string,
    ): EnrichedBenefitFieldMeta => ({
      btxFieldName,
      value: "parse_error",
      pageNumber: null,
      justification: "",
      network: null,
      networkName: null,
      optionName: null,
      highAccuracy: false,
      source: "extraction",
      documentDownloadUrl: null,
      benefitPlanCreatedAt: null,
      planName: "",
      documentName: null,
    });

    const extraEntries: Record<string, EnrichedBenefitFieldMeta> = {};
    for (const { benefitPlanId, fields } of parseErrors) {
      for (const field of fields) {
        if (field === "requested") {
          extraEntries[`${benefitPlanId}::requestedCommissionType`] =
            parseErrorMeta("requestedCommissionType");
          extraEntries[`${benefitPlanId}::requestedCommissionAmount`] =
            parseErrorMeta("requestedCommissionAmount");
        } else if (field === "current") {
          extraEntries[`${benefitPlanId}::currentCommissionType`] =
            parseErrorMeta("currentCommissionType");
          extraEntries[`${benefitPlanId}::currentCommissionAmount`] =
            parseErrorMeta("currentCommissionAmount");
        }
      }
    }
    return { ...extraEntries, ...aiFieldLookup };
  }, [aiFieldLookup, rfp?.broker?.parseErrors]);

  // Fetch benefit plan groups for the selected coverage
  // Use coverageType (V5 API type) and productType for correct filtering
  const {
    data: benefitPlanGroups = [],
    refetch: refetchBenefitPlanGroups,
    isLoading: isLoadingBenefitPlanGroups,
  } = useListBenefitPlanGroups(
    {
      rfpId: rfpDetails?.ecoRfpId ?? "",
      coverageType: selectedCoverage?.coverageType ?? "",
      productType: selectedCoverage?.productType,
    },
    axiosInstance,
    {
      enabled:
        !!rfpDetails?.ecoRfpId &&
        !!selectedCoverage?.coverageType &&
        !isWholeLife,
    },
  );
  // When BTX has plans not yet in the RFP query result (skeletons),
  // force a refetch to replace them with real data and avoid false AI indicators.
  useEffect(() => {
    if (!rfpDetails?.ecoRfpId || !benefitPlanGroups.length) return;

    const btxPlanIds = new Set(
      benefitPlanGroups.flatMap((group) =>
        group.benefitPlans.map((benefitPlan) => benefitPlan.id),
      ),
    );
    const rfpPlanIds = new Set(
      selectedCoverage?.benefitPlans?.map((benefitPlan) => benefitPlan.id) ??
        [],
    );

    const hasMissingPlans = [...btxPlanIds].some((id) => !rfpPlanIds.has(id));

    if (hasMissingPlans) {
      const rfpQueryKey = queryKeys.rfp(rfpDetails.ecoRfpId);
      if (queryClient.isFetching({ queryKey: rfpQueryKey }) === 0) {
        void queryClient.refetchQueries({ queryKey: rfpQueryKey });
      }
    }
  }, [
    benefitPlanGroups,
    selectedCoverage?.benefitPlans,
    rfpDetails?.ecoRfpId,
    queryClient,
  ]);

  // Memoized so a benign re-render (e.g. updating the "last saved" label after a
  // rate edit) doesn't produce a new array reference. An unstable list here
  // flows into aiFieldsByPlanId → the RatesGrid cell renderer → its column defs,
  // which remounts every rate cell and refetches every plan's rate schedule.
  const benefitPlanIds = useMemo(
    () =>
      benefitPlanGroups.flatMap((group) =>
        group.benefitPlans.map((plan) => plan.id),
      ),
    [benefitPlanGroups],
  );

  // Build a per-plan lookup of combined AI extraction metadata for the RatesGrid indicator
  const aiFieldsByPlanId = useMemo(
    () => buildAiFieldsByPlanId(aiFieldLookup, benefitPlanIds),
    [aiFieldLookup, benefitPlanIds],
  );

  // Benefit plan groups only give us plan/group timestamps.
  // To keep the "last saved" label accurate after rate edits and refreshes,
  // we also need the per-plan rate schedule data because the freshest change
  // can live on a nested rate or rate schedule updatedAt.
  const rateScheduleQueries = useQueries({
    queries: benefitPlanIds.map((benefitPlanId) => ({
      queryKey: rateQueryKeys.schedules(benefitPlanId),
      queryFn: () => listRateSchedules(benefitPlanId, axiosInstance),
      enabled:
        !!axiosInstance &&
        !!selectedCoverage?.coverageType &&
        !isLoadingBenefitPlanGroups,
      staleTime: RATE_SCHEDULES_STALE_TIME_MS,
    })),
  });

  const rateSchedules = rateScheduleQueries.flatMap(
    (query) => query.data ?? [],
  );

  const isLoadingRateSchedules = rateScheduleQueries.some(
    (query) => query.isPending && !query.data,
  );

  const effectiveLastSavedAt = getEffectiveLastSavedAt({
    rfpLastModified: rfpWithName?.lastModified,
    isLoading: isLoadingBenefitPlanGroups || isLoadingRateSchedules,
    benefitPlanGroups,
    rateSchedules,
    lastSavedAtOverride,
  });

  // Debug: Log whenever benefitPlanGroups or RFP data changes
  useEffect(() => {
    logger.info("[WorkbenchBuild2] Data changed:", {
      benefitPlanGroupsLength: benefitPlanGroups.length,
      benefitPlanGroupIds: benefitPlanGroups.map((g) => g.id),
      rfpCoveragesLength: rfpWithName?.coverages?.length ?? 0,
      hasRfp: !!rfpWithName,
      selectedCoverageStatus,
    });
  }, [benefitPlanGroups, rfpWithName, selectedCoverageStatus]);

  // Hide the RatesGrid and SummaryGrid when ALL plans lack rates
  // (newBusiness and alternate plans don't have rate schedules)
  const shouldHideRates =
    isLoadingBenefitPlanGroups ||
    isWholeLife ||
    allPlansLackRates(benefitPlanGroups);

  // Dental PPO splits each plan into inNetwork / outOfNetwork sub-columns in
  // PlanDesigner. RatesGrid must mirror that structure for alignedGrids to sync.
  const ratesNetworks = useMemo((): readonly string[] => {
    const isPPO = benefitPlanGroups[0]?.productType === "ppo";
    const isDental = selectedCoverage?.coverageType === CoverageTypeEnum.dental;
    return isPPO && isDental ? ["inNetwork", "outOfNetwork"] : [];
  }, [selectedCoverage?.coverageType, benefitPlanGroups]);

  const handleRatesError = useCallback(
    (message: string) => {
      createToastNotification({ message, severity: "error" });
    },
    [createToastNotification],
  );

  const handleRateSaved = useCallback(
    (benefitPlanGroupId?: string): void => {
      // The rate mutation already writes the new value into the rate-schedule
      // cache via setQueryData, so the edited cell reflects it without any
      // refetch. We deliberately do NOT refetch the benefit-plan-groups query
      // here: it returns new object references that rebuild the RatesGrid
      // columns and remount every rate cell, which both wipes in-flight edits
      // and refetches every plan's rate schedule.
      //
      // Instead, refresh just the backend-computed premiums via the lightweight
      // per-group premiums endpoint. SummaryGrid observes these queries, so
      // invalidating them updates the summary numbers without touching the
      // editable grid. Scope the invalidation to the edited group so only that
      // group's premiums refetch, not every group's (XTRM-2024).
      setLastSavedAtOverride(new Date());
      void queryClient.invalidateQueries({
        queryKey: benefitPlanGroupId
          ? benefitPlanGroupQueryKeys.premium(benefitPlanGroupId)
          : benefitPlanGroupQueryKeys.premiums(),
      });
    },
    [queryClient],
  );

  const handleDrawerSaveSuccess = useCallback((): void => {
    setLastSavedAtOverride(new Date());
    refetchBenefitPlanGroups();
    handleRateSaved();
  }, [refetchBenefitPlanGroups, handleRateSaved]);

  const handleToggleFullScreen = useCallback((): void => {
    setIsFullScreen((prev) => !prev);
  }, []);

  useExitFullScreenOnEscape(isFullScreen, () => setIsFullScreen(false));

  // Bumped on every preset click — including re-clicking the active preset —
  // so the grids reset manually dragged columns back to preset sizing.
  const [columnSizingResetToken, setColumnSizingResetToken] = useState(0);
  const handleColumnWidthChange = useCallback(
    (preset: ColumnWidthPreset): void => {
      setColumnWidth(preset);
      setColumnSizingResetToken((token) => token + 1);
    },
    [],
  );

  // Plan-column width applied to all three aligned grids via their column defs.
  const columnWidthScale = COLUMN_WIDTH_SCALE[columnWidth];

  const isNewBusinessCoverage = useMemo(
    () =>
      (selectedCoverage?.benefitPlans ?? []).length > 0 &&
      (selectedCoverage?.benefitPlans ?? []).every(
        (plan) =>
          plan.policyPeriod === "newBusiness" ||
          plan.policyPeriod === "alternate",
      ),
    [selectedCoverage?.benefitPlans],
  );

  const indicatorMode = resolveIndicatorMode({
    hasBenchmarkSeededPlans,
    isNewBusinessCoverage,
  });

  const indicatorProps = useMemo(() => {
    const aiAutoCount =
      indicatorMode === "ai" ? (
        <AIAutoCount
          count={aiAutoCountValues.count}
          total={aiAutoCountValues.total}
        />
      ) : undefined;

    const tableLegend =
      indicatorMode === "ai" ? (
        <TableLegend />
      ) : (
        <TableLegend mode="benchmark" />
      );

    return {
      aiAutoCount,
      tableLegend,
      useCoverageStatusSelect: true,
      CellRenderer: AiStatusCellRenderer,
      planDocumentMap: indicatorMode === "ai" ? planDocumentMap : undefined,
    };
  }, [indicatorMode, aiAutoCountValues, planDocumentMap]);

  const latestRowDataRef = useRef<unknown[]>([]);

  const transformPlanEditorRowData = useMemo(() => {
    const excludedAiHintBenefitKeys = commissionPrepopulationEnabled
      ? new Set(["employerContribution"])
      : undefined;

    return (rowData: unknown[]): unknown[] => {
      latestRowDataRef.current = rowData;

      return rowData.map((row) => ({
        ...(row as object),
        meta: {
          ...((row as { meta?: object }).meta ?? {}),
          aiFieldLookup: aiFieldLookupWithParseErrors,
          documentProcessing: anyDocumentProcessing,
          benchmarkSeededPlanIds,
          excludedAiHintBenefitKeys,
          enableCellValidation: true,
          enableHighAccuracy: true,
        },
      }));
    };
  }, [
    aiFieldLookupWithParseErrors,
    anyDocumentProcessing,
    benchmarkSeededPlanIds,
    commissionPrepopulationEnabled,
  ]);

  useEffect(() => {
    const counts = countAiAutoFields({
      rowData: latestRowDataRef.current,
      aiFieldLookup,
      benefitPlanIds,
      benefitPlanGroups,
      aiFieldsByPlanId,
      includeRatesAndSummary: !shouldHideRates,
    });

    setAiAutoCountValues((prev) => {
      if (prev.count === counts.count && prev.total === counts.total)
        return prev;
      return counts;
    });
  }, [
    aiFieldLookup,
    benefitPlanIds,
    benefitPlanGroups,
    aiFieldsByPlanId,
    shouldHideRates,
  ]);

  // Stable references for the heavy grid children. Without memoization a save
  // re-renders this component with fresh object/array/callback identities, which
  // rebuilds the PlanEditor/RatesGrid columns and remounts their cells and
  // headers — refetching rate schedules and candidate policies unnecessarily.
  const planDesignerConfiguration = useMemo(
    () => ({
      axiosInstance,
      featureFlags: {
        enableNeedsReviewPanel: enabledBTXReviewPanel,
        newDeletePlanValidation,
        enableCellValidation: true,
        enableHighAccuracy: true,
      },
      ui: {
        utilityBar: {
          showCoverageSelector: false,
        },
      },
    }),
    [axiosInstance, enabledBTXReviewPanel, newDeletePlanValidation],
  );

  const planDesignerAlignedGrids = useMemo(
    () => [ratesGridRef, summaryGridRef],
    [ratesGridRef, summaryGridRef],
  );
  const ratesAlignedGrids = useMemo(
    () => [planDesignerGridRef, summaryGridRef],
    [planDesignerGridRef, summaryGridRef],
  );
  const summaryAlignedGrids = useMemo(
    () => [planDesignerGridRef, ratesGridRef],
    [planDesignerGridRef, ratesGridRef],
  );

  const handleLastSaved = useCallback((savedAt: Date): void => {
    setLastSavedAtOverride(savedAt);
  }, []);

  const isWaitingForAiFields =
    !isLoadingPlanBuilderData && documentIds.length > 0 && isLoadingAiFields;

  if (
    isLoadingDetails ||
    isLoadingRfp ||
    isLoadingBenefitPlanGroups ||
    isLoadingPlanBuilderData ||
    isWaitingForAiFields
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
        <Typography variant="body-large" sx={{ ml: 2 }}>
          Loading Build 2.0...
        </Typography>
      </Box>
    );
  }

  if (rfpError || !rfpWithName) {
    return (
      <Box>
        <Alert severity="error">
          {rfpError?.message || "Failed to initialize Build 2.0"}
        </Alert>
      </Box>
    );
  }

  const handleBack = (): void => {
    navigate("..");
  };

  const hiddenCount = selectedCoverage
    ? hiddenColumnsCount(`plan-editor-${selectedCoverage.type}`)
    : 0;

  const openRatesDrawer = (
    group: BenefitPlanGroup,
    plan: BenefitPlan,
  ): void => {
    setRatesDrawerSelection({
      benefitPlanGroup: group,
      benefitPlan: plan,
    });
    setRatesDrawerOpen(true);
  };

  const closeRatesDrawer = (): void => {
    setRatesDrawerOpen(false);
    setRatesDrawerSelection(undefined);
  };

  const handleCoverageSelect = (coverageType: string): void => {
    // Use an absolute path so the coverage switch lands on the same route shape
    // the document library navigates to.
    // Push (not replace) so each coverage switch is its own history entry
    // and the back button returns to the previous coverage.
    setLastSavedAtOverride(null);
    navigate(
      `/${ROUTE_NAMES.RFPS}/${rfpId}/${ROUTE_NAMES.RFP_WORKBENCH_BUILD_2}/${ROUTE_NAMES.RFP_WORKBENCH_BUILD_2_FORMS}/${coverageType}`,
    );
  };

  return (
    <RatesErrorProvider value={handleRatesError}>
      {/*
       * AG Grid renders its in-grid dropdowns into document.body (popupParent)
       * with a low z-index (~5), so the full-screen overlay paints over them and
       * they appear empty. While full screen is active, lift those popups above
       * the overlay. Scoped to the full-screen session; removed on exit.
       */}
      {isFullScreen && (
        <GlobalStyles
          styles={(theme) => ({
            // Target both the wrapper and the child: AG Grid's shipped CSS puts
            // the low z-index (5) on `.ag-popup-child`, so lifting only the
            // wrapper may not raise the actual rendered dropdown.
            ".ag-popup, .ag-popup-child": {
              zIndex: `${theme.zIndex.appBar + 2} !important`,
            },
          })}
        />
      )}
      <Box
        sx={
          isFullScreen
            ? {
                position: "fixed",
                inset: 0,
                zIndex: (theme) => theme.zIndex.appBar + 1,
                overflow: "auto",
                bgcolor: "background.default",
              }
            : undefined
        }
      >
        {selectedCoverage ? (
          <>
            <PlanDesigner
              ref={planDesignerGridRef}
              coverage={selectedCoverage}
              rfp={rfpWithName}
              status={selectedCoverageStatus}
              isFullScreen={isFullScreen}
              onToggleFullScreen={handleToggleFullScreen}
              columnWidth={columnWidth}
              onColumnWidthChange={handleColumnWidthChange}
              columnWidthScale={columnWidthScale}
              columnSizingResetToken={columnSizingResetToken}
              onBack={handleBack}
              onCoverageSelect={handleCoverageSelect}
              alignedGrids={planDesignerAlignedGrids}
              onAlignmentMetricsChange={handlePlanAlignmentMetricsChange}
              aiAutoCount={indicatorProps.aiAutoCount}
              tableLegend={indicatorProps.tableLegend}
              useCoverageStatusSelect={indicatorProps.useCoverageStatusSelect}
              CellRenderer={indicatorProps.CellRenderer}
              planDocumentMap={indicatorProps.planDocumentMap}
              transformRowData={transformPlanEditorRowData}
              warningBanner={
                <>
                  {hasBenchmarkSeededPlans && rfpId && selectedCoverage && (
                    <BenchmarkBanner
                      rfpId={rfpId}
                      coverageType={selectedCoverage.type}
                    />
                  )}
                  {hiddenCount > 0 && (
                    <HiddenColumnsBanner hiddenCount={hiddenCount} />
                  )}
                </>
              }
              validationConfig={validationConfig}
              lastSavedAt={effectiveLastSavedAt}
              onLastSaved={handleLastSaved}
              configuration={planDesignerConfiguration}
            />
            {!shouldHideRates && (
              <BuildRateCellReviewProvider
                benefitPlanGroups={benefitPlanGroups}
                axiosInstance={axiosInstance}
                // selectedCoverage is derived from the loaded RFP.
                rfpId={rfpWithName!.id}
                aiFieldsByPlanId={aiFieldsByPlanId}
              >
                {/* Separator between PlanDesigner and RatesGrid */}
                <Box
                  sx={{
                    height: "5px",
                    width: "100%",
                    backgroundColor: "#00ACC1", // Turquoise/teal separator color from Figma
                    margin: 0,
                    padding: 0,
                  }}
                />
                <Box sx={{ paddingRight: `${planRightGutter}px` }}>
                  <RatesGrid
                    ref={ratesGridRef}
                    columnWidthScale={columnWidthScale}
                    columnWidths={planColumnWidths}
                    alignedGrids={ratesAlignedGrids}
                    benefitPlanGroups={benefitPlanGroups}
                    onOpenDrawer={(ctx) => {
                      openRatesDrawer(ctx.benefitPlanGroup, ctx.benefitPlan);
                    }}
                    axiosInstance={axiosInstance}
                    coverageType={decodedPlanName ?? undefined}
                    onRateSaved={handleRateSaved}
                    aiFieldsByPlanId={aiFieldsByPlanId}
                    networks={ratesNetworks}
                    enableCellValidation
                  />
                </Box>
                {/* Separator between RatesGrid and SummaryGrid */}
                <Box
                  sx={{
                    height: "5px",
                    width: "100%",
                    backgroundColor: "#00ACC1", // Turquoise/teal separator color from Figma
                    margin: 0,
                    padding: 0,
                  }}
                />
                <Box sx={{ paddingRight: `${planRightGutter}px` }}>
                  <SummaryGrid
                    ref={summaryGridRef}
                    columnWidthScale={columnWidthScale}
                    columnWidths={planColumnWidths}
                    alignedGrids={summaryAlignedGrids}
                    benefitPlanGroups={benefitPlanGroups}
                    axiosInstance={axiosInstance}
                  />
                </Box>
              </BuildRateCellReviewProvider>
            )}

            <RatesDrawer
              open={isRatesDrawerOpen}
              onClose={closeRatesDrawer}
              title="Rates"
              benefitPlanGroup={ratesDrawerSelection?.benefitPlanGroup}
              initialBenefitPlanId={ratesDrawerSelection?.benefitPlan?.id}
              axiosInstance={axiosInstance}
              onSaveSuccess={handleDrawerSaveSuccess}
              initializeWithDefaultRates
            />
          </>
        ) : (
          <Alert severity="warning">
            No coverage data available in the RFP
          </Alert>
        )}
      </Box>
    </RatesErrorProvider>
  );
};

export default WorkbenchBuild2;
